import {
  buildRichTooltip,
  RICH_TOOLTIP_CLASS,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../_shared/_logic/rich-tooltips"

export const WORK_ORDER_ACTION_TOOLTIP_CLASS = RICH_TOOLTIP_CLASS

type ActionTooltipParts = {
  title: string
  action: string
  effect?: string
  hint?: string
  prerequisite?: string
}

const actionTooltip = ({ title, action, effect, hint, prerequisite }: ActionTooltipParts): string =>
  buildRichTooltip([
    tooltipTitle(title),
    tooltipField("Was passiert", action),
    tooltipField("Auswirkung", effect),
    tooltipField("Voraussetzung", prerequisite),
    tooltipText(hint),
  ])

export const workOrderActionTooltipWithDisabledReason = (
  tooltipContent: string,
  disabledReason?: string | null,
): string => (disabledReason ? buildRichTooltip([tooltipText(disabledReason)]) : tooltipContent)

export const workOrderActionTooltips = {
  rowContactPlanning: tooltipText(
    "Termin und Kundenankündigung für diesen Auftrag planen oder bearbeiten.",
  )!,
  rowComplete: actionTooltip({
    title: "Auftrag als erledigt markieren",
    action: "Als abgeschlossen markieren, nachdem die Arbeit erledigt ist.",
    effect:
      "Kunden können für einen abgeschlossenen Auftrag keine Termine mehr vereinbaren, bestätigen oder verschieben.",
    hint: "Sie können den Auftrag später wieder öffnen, falls er doch weiter bearbeitet werden soll.",
  }),
  rowReopen: actionTooltip({
    title: "Auftrag wieder öffnen",
    action: "Öffnet einen abgeschlossenen Auftrag wieder zur Bearbeitung.",
    effect:
      "Der Auftrag kann danach wieder geplant, angekündigt und über die Kundenseite bearbeitet werden.",
  }),
  rowArchive: actionTooltip({
    title: "Auftrag archivieren",
    action: "Blendet den Auftrag dauerhaft aus normalen Listen und neuen Abläufen aus.",
    effect:
      "Offene Termine und laufende Ankündigungsprozesse werden beendet. Bereits berechnete Credits bleiben bestehen.",
    hint: "Der Kunde wird nicht automatisch informiert. Der Auftrag kann nicht wiederhergestellt werden.",
  }),
  batchComplete: actionTooltip({
    title: "Ausgewählte Aufträge erledigen",
    action: "Markiert alle ausgewählten Aufträge als abgeschlossen.",
    effect:
      "Für diese Aufträge können Kunden danach keine Termine mehr vereinbaren, bestätigen oder verschieben.",
    hint: "Übersprungene oder fehlgeschlagene Aufträge werden nach der Aktion gemeldet.",
  }),
  batchArchive: actionTooltip({
    title: "Ausgewählte Aufträge archivieren",
    action: "Blendet alle ausgewählten Aufträge dauerhaft aus normalen Listen aus.",
    effect:
      "Offene Termine und laufende Ankündigungsprozesse werden beendet. Bereits berechnete Credits bleiben bestehen.",
    hint: "Die Kunden werden nicht automatisch informiert. Archivierte Aufträge können nicht wiederhergestellt werden.",
  }),
  batchReschedule: actionTooltip({
    title: "Termine verschieben",
    action: "Öffnet die Sammelverschiebung für ausgewählte Aufträge mit verschiebbaren Terminen.",
    effect:
      "Die bisherigen Termine werden ersetzt und die neuen Termine können anschließend wieder angekündigt werden.",
  }),
  batchConfirmAppointments: actionTooltip({
    title: "Termine bestätigen",
    action: "Markiert die ausgewählten Termine als vom Kunden bestätigt.",
    effect:
      "Im nächsten Schritt können Sie optional Bestätigungsmails an erreichbare Kundenkontakte senden.",
  }),
  batchCreateAppointments: actionTooltip({
    title: "Termine anlegen",
    action: "Plant Termine für ausgewählte Aufträge, die noch keinen passenden Termin haben.",
    effect:
      "Die Terminplanung nutzt Arbeitszeiten, bestehende Termine und die aktuelle Planungslogik.",
  }),
  batchCreateAnnouncements: actionTooltip({
    title: "Ankündigungspläne anlegen",
    action: "Erstellt Kundeninformationen für ausgewählte Aufträge mit Termin.",
    effect: "KehrBlatt bereitet die für den Tarif verfügbaren Kontaktwege vor.",
  }),
  batchCancelAnnouncements: actionTooltip({
    title: "Ankündigungspläne beenden",
    action:
      "Wählen Sie, wie die laufenden Kundenankündigungen der ausgewählten Aufträge beendet werden sollen.",
    effect:
      "Im folgenden Fenster entscheiden Sie zwischen Abbrechen und Als abgeschlossen markieren.",
  }),
  headerPrint: tooltipText(
    "Terminzeitraum und Mitarbeiter für den Druck wählen. Der Tourenzettel enthält Auftragstermine sowie interne Blocker, Abwesenheiten und externe Belegt-Termine. Höchstens 500 Einträge insgesamt.",
  )!,
  headerBatchPlanning: tooltipText(
    "Rückstandsliste als CSV oder PDF laden oder Liegenschaften auswählen. Danach Aufträge anlegen, Termine planen und Kunden informieren.",
  )!,
  headerCreate: tooltipText("Für einen bestehenden oder neuen Auftrag einen Termin planen.")!,
  detailAppointmentPage: tooltipText("Öffnet die Terminseite aus Sicht des Kunden.")!,
  detailMarkFailed: actionTooltip({
    title: "Erfolglos abschließen",
    action: "Schließt den Auftrag ohne erfolgreiche Durchführung ab und speichert einen Grund.",
    effect:
      "Der Auftrag gilt danach als abgeschlossen, ohne dass eine reguläre Erledigung dokumentiert wird.",
    prerequisite: "Der Auftrag ist offen und überfällig.",
  }),
  lifecycleAppointment: tooltipText("Zeit, Mitarbeiter und Termindetails bearbeiten.")!,
  lifecycleAnnouncement: actionTooltip({
    title: "Ankündigungsplan starten",
    action: "Startet den Ankündigungsplan für den geplanten Termin.",
    effect:
      "KehrBlatt bereitet die Kundeninformation anhand von Termin, Auftrag und Ansprechpartnern vor.",
  }),
  printRemoveRow: tooltipText("Nur aus diesem Drucklauf entfernen; der Auftrag bleibt erhalten.")!,
  printStart: actionTooltip({
    title: "Drucken",
    action: "Startet den Druckauftrag für die ausgewählten Briefe und Klebezettel.",
    effect:
      "KehrBlatt erstellt die PDF-Dateien im Hintergrund und zeigt den Fortschritt im nächsten Schritt.",
  }),
  printKeepAnnouncementOpen: actionTooltip({
    title: "Ankündigungsplan offen lassen",
    action: "Entfernt diesen Auftrag aus der Auswahl zum Schließen von Ankündigungsplänen.",
    effect: "Der Ankündigungsplan bleibt nach dem Druck weiter offen.",
  }),
  printCloseAnnouncements: actionTooltip({
    title: "Ankündigungspläne schließen",
    action: "Schließt die ausgewählten Ankündigungspläne nach dem Druck.",
    effect:
      "Die betroffenen Pläne gelten danach als erledigt; nicht ausgewählte Pläne bleiben offen.",
  }),
  batchLoadBacklogList: actionTooltip({
    title: "Rückstandsliste übernehmen",
    action:
      "Prüft Ihre CSV- oder PDF-Datei und erkennt das Listenformat. Danach wählen Sie die gewünschten Arbeiten.",
    effect:
      "Erst beim Übernehmen werden Liegenschaften und Einheiten zugeordnet. Aufträge legen Sie anschließend in der Planungstabelle an.",
  }),
  batchEditAddress: actionTooltip({
    title: "Adresse anlegen",
    action: "Öffnet die Liegenschaftsmaske für diese Zeile.",
    effect:
      "Sie können fehlende Liegenschaften anlegen, damit sie in der Sammelplanung verwendet werden können.",
  }),
  batchEditUnits: tooltipText("Einheiten hinzufügen oder ihre Bezeichnung ändern.")!,
  batchEditAppointment: tooltipText("Zeit, Mitarbeiter und Termindetails bearbeiten.")!,
  batchCreateContactOrders: actionTooltip({
    title: "Ankündigungspläne anlegen",
    action: "Legt Ankündigungspläne für alle offenen Zeilen mit aktivem Kontaktweg an.",
    effect: "Zeilen ohne aktiven Kontaktweg werden übersprungen.",
  }),
  batchNoContactOrdersAvailable: actionTooltip({
    title: "Keine aktiven Kontaktwege",
    action: "Für die offenen Termine ist kein Ankündigungsplan aktiv.",
    effect: "Aktivieren Sie mindestens einen Kontaktweg, um Ankündigungspläne anzulegen.",
  }),
  batchEditAnnouncementRecipients: tooltipText(
    "Namen, E-Mail-Adressen und Telefonnummern für die Ankündigung ergänzen.",
  )!,
} as const

export const buildCompleteWorkOrderApprovalDescription = (count: number): string => {
  if (count === 1) {
    return "1 Auftrag als abgeschlossen markieren? Danach kann der Kunde für diesen Auftrag keinen Termin mehr vereinbaren, bestätigen oder ändern. Nutzen Sie das nur, wenn der Auftrag wirklich erledigt ist."
  }

  return `${count} Aufträge als abgeschlossen markieren? Danach können Kunden für diese Aufträge keine Termine mehr vereinbaren, bestätigen oder ändern. Nutzen Sie das nur, wenn die Aufträge wirklich erledigt sind.`
}

export const buildArchiveWorkOrderApprovalDescription = (count: number): string => {
  if (count === 1) {
    return "Diesen Auftrag endgültig archivieren? Er verschwindet aus den normalen Listen und kann nicht wiederhergestellt werden. Offene Termine und laufende Ankündigungsprozesse werden beendet. Der Kunde wird nicht automatisch informiert. Bereits berechnete Credits werden nicht erstattet."
  }

  return `${count} Aufträge endgültig archivieren? Sie verschwinden aus den normalen Listen und können nicht wiederhergestellt werden. Offene Termine und laufende Ankündigungsprozesse werden beendet. Die Kunden werden nicht automatisch informiert. Bereits berechnete Credits werden nicht erstattet.`
}
